import { useState } from 'react';
import { Button, Dialog, Flex, IconButton, Text, TextArea } from '@radix-ui/themes';
import { Check, History, Plus, Trash2 } from 'lucide-react';
import { getTelegramWebApp } from '../lib/telegram';

/**
 * One-tap prompts, plus the door to history.
 *
 * The premise: a phone keyboard is the slowest part of this whole system, so
 * anything sent more than once should never be typed twice.
 */
export function Snippets({
  items,
  history,
  disabled,
  draft,
  onRun,
  onPick,
  onChange,
}: {
  items: string[];
  history: string[];
  disabled: boolean;
  /** Current composer text — what the + button saves. */
  draft: string;
  onRun: (text: string) => void;
  /** Put text into the composer without sending — for editing before use. */
  onPick: (text: string) => void;
  onChange: (items: string[]) => void;
}) {
  const [editing, setEditing] = useState(false);
  const hasDraft = draft.trim().length > 0;

  const remove = (text: string) => {
    const wa = getTelegramWebApp();
    const go = (yes: boolean) => yes && onChange(items.filter((i) => i !== text));
    if (wa?.showConfirm) wa.showConfirm(`Удалить «${text}»?`, go);
    else go(window.confirm(`Удалить «${text}»?`));
  };

  const saveDraft = () => {
    const t = draft.trim();
    if (!t || items.includes(t)) return;
    onChange([...items, t]);
    getTelegramWebApp()?.HapticFeedback?.notificationOccurred('success');
  };

  return (
    <div className="keybar" style={{ paddingBottom: 0 }}>
      {/* History first: it is the one that grows on its own and needs no upkeep. */}
      <Dialog.Root>
        <Dialog.Trigger>
          <IconButton size="1" variant="soft" color="gray" disabled={!history.length} title="История промптов">
            <History size={14} />
          </IconButton>
        </Dialog.Trigger>
        <Dialog.Content size="2" style={{ maxWidth: 460 }}>
          <Dialog.Title size="3">История</Dialog.Title>
          <Flex direction="column" gap="1" mt="2" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {history.map((h, i) => (
              <Dialog.Close key={`${h}-${i}`}>
                <button className="project-row" onClick={() => onPick(h)}>
                  <Text size="2" style={{ flex: 1, textAlign: 'left' }}>
                    {h}
                  </Text>
                </button>
              </Dialog.Close>
            ))}
          </Flex>
          <Text size="1" mt="3" style={{ color: 'var(--app-hint)', display: 'block' }}>
            Тап подставит текст в поле — можно поправить перед отправкой.
          </Text>
        </Dialog.Content>
      </Dialog.Root>

      {items.map((s) => (
        <Button
          key={s}
          size="1"
          variant="surface"
          color={editing ? 'red' : 'jade'}
          disabled={disabled && !editing}
          onClick={() => (editing ? remove(s) : onRun(s))}
        >
          {editing && <Trash2 size={12} />}
          <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s}
          </span>
        </Button>
      ))}

      {/* One button, two obvious jobs: save what you typed, or leave edit mode. */}
      <IconButton
        size="1"
        variant="soft"
        color={editing ? 'jade' : 'gray'}
        onClick={() => {
          if (editing) setEditing(false);
          else if (hasDraft) saveDraft();
          else setEditing(true);
        }}
        title={editing ? 'Готово' : hasDraft ? 'Сохранить текст из поля' : 'Редактировать список'}
      >
        {editing ? <Check size={14} /> : hasDraft ? <Plus size={14} /> : <Trash2 size={14} />}
      </IconButton>
    </div>
  );
}
